import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../lib/api';
import { useToast } from '../../components/ToastProvider';

interface Attachment {
  fileId?: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
  fileData?: string;
  extractedText?: string;
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  attachments?: Attachment[];
}

interface PendingFile {
  file: File;
  preview?: string;
  base64Data?: string;
}

interface Conversation {
  _id: string;
  title: string;
  lastMessage: string;
  messageCount: number;
  updatedAt: string;
}

export function FreeModeChat() {
  const { user, token, refresh } = useAuth();
  const { push } = useToast();

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [isCreatingConversation, setIsCreatingConversation] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingMessageRef = useRef<string | null>(null);

  // Tipos de arquivo suportados
  const SUPPORTED_TYPES = [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/msword',
    'text/plain',
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp'
  ];

  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

  // Helper para formatar tamanho do arquivo
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Helper para obter ícone do arquivo
  const getFileIcon = (mimeType: string): string => {
    if (mimeType.startsWith('image/')) return 'fa-image';
    if (mimeType === 'application/pdf') return 'fa-file-pdf';
    if (mimeType.includes('word') || mimeType.includes('document')) return 'fa-file-word';
    if (mimeType === 'text/plain') return 'fa-file-lines';
    return 'fa-file';
  };

  // Helper para obter cor do ícone
  const getFileIconColor = (mimeType: string): string => {
    if (mimeType.startsWith('image/')) return 'text-purple-400';
    if (mimeType === 'application/pdf') return 'text-red-400';
    if (mimeType.includes('word') || mimeType.includes('document')) return 'text-blue-400';
    if (mimeType === 'text/plain') return 'text-gray-400';
    return 'text-gray-400';
  };

  // Processar arquivo para base64
  const processFile = async (file: File): Promise<PendingFile | null> => {
    if (!SUPPORTED_TYPES.includes(file.type)) {
      push(`Tipo de arquivo não suportado: ${file.name}`, 'error');
      return null;
    }

    if (file.size > MAX_FILE_SIZE) {
      push(`Arquivo muito grande: ${file.name} (máx. 10MB)`, 'error');
      return null;
    }

    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        const base64Data = result.split(',')[1]; // Remove o prefixo data:xxx;base64,

        const pendingFile: PendingFile = {
          file,
          base64Data,
          preview: file.type.startsWith('image/') ? result : undefined
        };
        resolve(pendingFile);
      };
      reader.onerror = () => {
        push(`Erro ao ler arquivo: ${file.name}`, 'error');
        resolve(null);
      };
      reader.readAsDataURL(file);
    });
  };

  // Handler para seleção de arquivos
  const handleFileSelect = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const newFiles: PendingFile[] = [];
    for (let i = 0; i < files.length; i++) {
      const processed = await processFile(files[i]);
      if (processed) newFiles.push(processed);
    }

    if (newFiles.length > 0) {
      setPendingFiles(prev => [...prev, ...newFiles]);
    }
  };

  // Remover arquivo pendente
  const removeFile = (index: number) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    await handleFileSelect(e.dataTransfer.files);
  };

  // Auto-resize textarea
  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
    }
  }, []);

  // Handle keyboard events
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if ((inputMessage.trim() || pendingFiles.length > 0) && !isLoading) {
        sendMessage(e as any);
      }
    }
  };

  // Carregar conversas ao montar
  useEffect(() => {
    loadConversations();
  }, []);

  // Scroll automático para última mensagem
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Reset textarea height when message is cleared
  useEffect(() => {
    if (!inputMessage && textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  }, [inputMessage]);

  const loadConversations = async () => {
    if (!token) return;
    try {
      setLoadingConversations(true);
      const data = await api.listConversations(token);
      setConversations(data);
    } catch (error: any) {
      console.error('Erro ao carregar conversas:', error);
      push(error.message, 'error');
    } finally {
      setLoadingConversations(false);
    }
  };

  const loadConversation = async (id: string) => {
    if (!token) return;
    try {
      const data = await api.getConversation(id, token);
      setCurrentConversationId(id);
      setMessages(data.messages);
    } catch (error: any) {
      console.error('Erro ao carregar conversa:', error);
      push(error.message, 'error');
    }
  };

  const createNewConversation = async (): Promise<string | null> => {
    if (!token || isCreatingConversation) return null;

    try {
      setIsCreatingConversation(true);
      const data = await api.createConversation(token);
      setCurrentConversationId(data.conversationId);
      setMessages([]);
      await loadConversations();
      return data.conversationId;
    } catch (error: any) {
      console.error('Erro ao criar conversa:', error);
      push(error.message, 'error');
      return null;
    } finally {
      setIsCreatingConversation(false);
    }
  };

  const sendMessage = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!token) {
      console.error('[CHAT] Token não encontrado');
      return;
    }

    const hasFiles = pendingFiles.length > 0;
    if (!inputMessage.trim() && !hasFiles) {
      console.warn('[CHAT] Mensagem vazia e sem arquivos ignorada');
      return;
    }

    console.log('[CHAT] Iniciando envio de mensagem:', inputMessage, 'Arquivos:', pendingFiles.length);

    // Se não há conversa, criar uma nova
    let conversationId = currentConversationId;
    if (!conversationId) {
      console.log('[CHAT] Criando nova conversa...');
      const newConversationId = await createNewConversation();
      if (!newConversationId) {
        push('Erro ao criar conversa', 'error');
        return;
      }
      conversationId = newConversationId;
    }

    setIsLoading(true);
    const messageToSend = inputMessage;
    const filesToSend = [...pendingFiles];

    // Criar attachments para a UI
    const uiAttachments: Attachment[] = filesToSend.map(pf => ({
      fileName: pf.file.name,
      mimeType: pf.file.type,
      fileSize: pf.file.size,
      fileData: pf.preview
    }));

    // Adicionar mensagem do usuário imediatamente na UI
    const userMessage: Message = {
      role: 'user',
      content: messageToSend || (hasFiles ? 'Analise os arquivos anexados.' : ''),
      timestamp: new Date().toISOString(),
      attachments: uiAttachments.length > 0 ? uiAttachments : undefined
    };

    console.log('[CHAT] Adicionando mensagem do usuário na UI');
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setPendingFiles([]);

    try {
      // Preparar attachments para API
      const apiAttachments = filesToSend.map(pf => ({
        fileName: pf.file.name,
        mimeType: pf.file.type,
        base64Data: pf.base64Data!
      }));

      console.log('[CHAT] Enviando para API...');
      const data = await api.sendMessage(
        conversationId,
        messageToSend,
        token,
        apiAttachments.length > 0 ? apiAttachments : undefined
      );
      console.log('[CHAT] Resposta recebida:', data);

      // Adicionar resposta do assistente
      setMessages(prev => [...prev, data.message as Message]);

      // Atualizar dados do usuário (subscription usage)
      await refresh();

      // Recarregar lista de conversas (título pode ter mudado)
      await loadConversations();

      console.log('[CHAT] Mensagem enviada com sucesso!');
    } catch (error: any) {
      console.error('[CHAT] Erro ao enviar mensagem:', error);
      console.error('[CHAT] Stack:', error.stack);
      push(error.message || 'Erro ao enviar mensagem', 'error');

      // Remover mensagem do usuário em caso de erro
      setMessages(prev => prev.slice(0, -1));
      setInputMessage(messageToSend);
      setPendingFiles(filesToSend);
    } finally {
      setIsLoading(false);
    }
  };

  const clearAllMessages = async () => {
    if (!currentConversationId || !token) return;

    if (!confirm('Deseja limpar todo o histórico desta conversa? Esta ação não pode ser desfeita.')) return;

    try {
      await api.clearConversationMessages(currentConversationId, token);
      setMessages([]);
      push('Histórico limpo com sucesso', 'success');
    } catch (error: any) {
      console.error('Erro ao limpar histórico:', error);
      push(error.message || 'Erro ao limpar histórico', 'error');
    }
  };

  const deleteConversation = async (id: string) => {
    if (!token) return;
    if (!confirm('Deseja realmente deletar esta conversa?')) return;

    try {
      await api.deleteConversation(id, token);
      await loadConversations();

      if (currentConversationId === id) {
        setCurrentConversationId(null);
        setMessages([]);
      }

      push('Conversa deletada com sucesso', 'success');
    } catch (error: any) {
      console.error('Erro ao deletar conversa:', error);
      push(error.message, 'error');
    }
  };

  const copyMessage = (content: string) => {
    navigator.clipboard.writeText(content);
    push('Mensagem copiada!', 'success');
  };

  return (
    <div className="flex flex-col md:flex-row h-[calc(100vh-180px)] gap-4">
      {/* Sidebar de conversas */}
      <div className="w-full md:w-64 panel p-4 overflow-y-auto">
        <button
          onClick={createNewConversation}
          className="btn btn-primary w-full h-11 mb-4"
        >
          <i className="fa-solid fa-plus mr-2"></i>
          Nova Conversa
        </button>

        {loadingConversations ? (
          <div className="text-center py-8">
            <i className="fa-solid fa-spinner fa-spin opacity-50"></i>
          </div>
        ) : conversations.length === 0 ? (
          <div className="text-center py-8 opacity-60 text-xs">
            <i className="fa-solid fa-comments text-2xl mb-2"></i>
            <p>Nenhuma conversa ainda</p>
          </div>
        ) : (
          <div className="space-y-2">
            {conversations.map(conv => (
              <div
                key={conv._id}
                className={`group p-3 rounded-lg cursor-pointer transition-colors ${
                  currentConversationId === conv._id
                    ? 'bg-brand-500/10 border border-brand-500/30'
                    : 'hover:bg-[var(--cg-surface-alt)]'
                }`}
                onClick={() => loadConversation(conv._id)}
              >
                <div className="font-medium text-sm truncate mb-1">{conv.title}</div>
                <div className="text-xs opacity-60 truncate">{conv.lastMessage}</div>
                <div className="flex items-center justify-between mt-2">
                  <div className="text-[10px] opacity-50">
                    {new Date(conv.updatedAt).toLocaleDateString('pt-BR')}
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteConversation(conv._id);
                    }}
                    className="text-xs text-red-400 hover:text-red-300 opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Deletar conversa"
                  >
                    <i className="fa-solid fa-trash"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Área de chat */}
      <div className="flex-1 panel p-0 flex flex-col">
        {/* Header do chat */}
        <div className="p-4 border-b" style={{borderColor: 'var(--cg-border)'}}>
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h2 className="font-bold text-lg">
                <i className="fa-solid fa-wand-magic-sparkles text-brand-400 mr-2"></i>
                Copy Genius Chat
              </h2>
              <p className="text-xs opacity-60 mt-1">
                IA especializada em criar textos de alta conversão
              </p>
            </div>
            <div className="flex items-center gap-3">
              {currentConversationId && messages.length > 0 && (
                <button
                  onClick={clearAllMessages}
                  className="text-xs text-red-400 hover:text-red-300 transition-colors px-2 py-1 rounded hover:bg-red-400/10"
                  title="Limpar histórico"
                >
                  <i className="fa-solid fa-trash mr-1"></i>
                  Limpar
                </button>
              )}
              {user?.subscription && (
                <div className="text-sm">
                  <span className="opacity-60">Plano: </span>
                  <span className="font-semibold text-brand-400">{user.subscription.planName}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Mensagens */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {messages.length === 0 && !currentConversationId && (
            <div className="text-center py-20">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-brand-500/10 flex items-center justify-center">
                <i className="fa-solid fa-wand-magic-sparkles text-brand-400 text-3xl"></i>
              </div>
              <h3 className="text-xl font-bold mb-2">Copy Genius Chat</h3>
              <p className="opacity-70 mb-6 max-w-md mx-auto">
                Olá! Sou o Copy Genius, sua IA especializada em criar textos de alta conversão para marketing, vendas e comunicação empresarial. Como posso transformar suas ideias em copy poderoso hoje?
              </p>
              <button
                onClick={createNewConversation}
                className="btn btn-primary h-11 px-6"
              >
                <i className="fa-solid fa-plus mr-2"></i>
                Iniciar Conversa
              </button>
            </div>
          )}

          {/* Mensagem de boas-vindas quando inicia conversa */}
          {messages.length === 0 && currentConversationId && (
            <div className="flex justify-start">
              <div className="max-w-[80%] panel-alt p-4 rounded-lg">
                <div className="flex items-start gap-2 mb-2">
                  <div className="w-6 h-6 rounded-full bg-brand-500/20 flex items-center justify-center">
                    <i className="fa-solid fa-robot text-xs"></i>
                  </div>
                  <div className="text-xs opacity-70 font-medium">Copy Genius</div>
                </div>
                <div className="whitespace-pre-wrap text-sm leading-relaxed">
                  Olá! Sou o Copy Genius, sua IA especializada em criar textos de alta conversão para marketing, vendas e comunicação empresarial. Como posso transformar suas ideias em copy poderoso hoje?
                </div>
              </div>
            </div>
          )}

          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} group`}
            >
              <div
                className={`max-w-[80%] p-4 rounded-2xl ${
                  msg.role === 'user'
                    ? 'bg-brand-500 text-white rounded-br-md'
                    : 'panel-alt rounded-bl-md'
                }`}
              >
                <div className="flex items-center justify-between gap-3 mb-2">
                  <div className="flex items-center gap-2">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                      msg.role === 'user' ? 'bg-white/20' : 'bg-brand-500/20'
                    }`}>
                      <i className={`fa-solid ${
                        msg.role === 'user' ? 'fa-user' : 'fa-robot'
                      } text-xs`}></i>
                    </div>
                    <div className="text-xs opacity-70 font-medium">
                      {msg.role === 'user' ? 'Você' : 'Copy Genius'}
                    </div>
                  </div>
                  <button
                    onClick={() => copyMessage(msg.content)}
                    className={`opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-md hover:bg-black/10 ${
                      msg.role === 'user' ? 'text-white/70 hover:text-white' : 'text-current opacity-50 hover:opacity-100'
                    }`}
                    title="Copiar mensagem"
                  >
                    <i className="fa-solid fa-copy text-xs"></i>
                  </button>
                </div>
                {/* Attachments */}
                {msg.attachments && msg.attachments.length > 0 && (
                  <div className="mb-3 space-y-2">
                    {msg.attachments.map((att, attIdx) => (
                      <div
                        key={attIdx}
                        className={`flex items-center gap-2 p-2 rounded-lg ${
                          msg.role === 'user' ? 'bg-white/10' : 'bg-[var(--cg-surface)]'
                        }`}
                      >
                        {att.fileData && att.mimeType.startsWith('image/') ? (
                          <img
                            src={att.fileData}
                            alt={att.fileName}
                            className="w-12 h-12 object-cover rounded"
                          />
                        ) : (
                          <div className={`w-10 h-10 rounded flex items-center justify-center ${
                            msg.role === 'user' ? 'bg-white/20' : 'bg-[var(--cg-surface-alt)]'
                          }`}>
                            <i className={`fa-solid ${getFileIcon(att.mimeType)} ${
                              msg.role === 'user' ? 'text-white/80' : getFileIconColor(att.mimeType)
                            }`}></i>
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className={`text-xs font-medium truncate ${
                            msg.role === 'user' ? 'text-white/90' : ''
                          }`}>
                            {att.fileName}
                          </div>
                          <div className={`text-[10px] ${
                            msg.role === 'user' ? 'text-white/60' : 'opacity-50'
                          }`}>
                            {formatFileSize(att.fileSize)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="whitespace-pre-wrap text-sm leading-relaxed">{msg.content}</div>
                <div className={`text-[10px] mt-2 ${
                  msg.role === 'user' ? 'opacity-70' : 'opacity-50'
                }`}>
                  {new Date(msg.timestamp).toLocaleTimeString('pt-BR', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              </div>
            </div>
          ))}

          {isLoading && (
            <div className="flex justify-start">
              <div className="panel-alt p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-6 h-6 rounded-full bg-brand-500/20 flex items-center justify-center">
                    <i className="fa-solid fa-robot text-xs"></i>
                  </div>
                  <div className="text-xs opacity-70 font-medium">CopyGenius</div>
                </div>
                <div className="flex space-x-2">
                  <div className="w-2 h-2 bg-brand-400 rounded-full animate-bounce"></div>
                  <div className="w-2 h-2 bg-brand-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                  <div className="w-2 h-2 bg-brand-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div
          className={`border-t p-4 transition-colors ${isDragging ? 'bg-brand-500/10' : ''}`}
          style={{borderColor: isDragging ? 'var(--cg-accent)' : 'var(--cg-border)'}}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {/* Preview de arquivos pendentes */}
          {pendingFiles.length > 0 && (
            <div className="mb-3 flex flex-wrap gap-2">
              {pendingFiles.map((pf, idx) => (
                <div
                  key={idx}
                  className="relative group bg-[var(--cg-surface-alt)] rounded-lg p-2 flex items-center gap-2 pr-8"
                >
                  {pf.preview ? (
                    <img
                      src={pf.preview}
                      alt={pf.file.name}
                      className="w-10 h-10 object-cover rounded"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded bg-[var(--cg-surface)] flex items-center justify-center">
                      <i className={`fa-solid ${getFileIcon(pf.file.type)} ${getFileIconColor(pf.file.type)}`}></i>
                    </div>
                  )}
                  <div className="max-w-[120px]">
                    <div className="text-xs font-medium truncate">{pf.file.name}</div>
                    <div className="text-[10px] opacity-50">{formatFileSize(pf.file.size)}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeFile(idx)}
                    className="absolute top-1 right-1 w-5 h-5 rounded-full bg-red-500/80 hover:bg-red-500 flex items-center justify-center text-white transition-colors"
                    title="Remover arquivo"
                  >
                    <i className="fa-solid fa-times text-[10px]"></i>
                  </button>
                </div>
              ))}
            </div>
          )}

          <form onSubmit={sendMessage} className="flex gap-3 items-end">
            {/* Input de arquivo oculto */}
            <input
              type="file"
              ref={fileInputRef}
              onChange={(e) => handleFileSelect(e.target.files)}
              className="hidden"
              multiple
              accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif,.webp"
            />

            {/* Botão de anexar */}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="btn h-12 w-12 flex-shrink-0 flex items-center justify-center hover:bg-[var(--cg-surface-alt)] transition-colors"
              title="Anexar arquivo (PDF, DOC, TXT, Imagem)"
              disabled={isLoading}
            >
              <i className="fa-solid fa-paperclip"></i>
            </button>

            <div className="flex-1">
              <textarea
                ref={textareaRef}
                value={inputMessage}
                onChange={(e) => {
                  setInputMessage(e.target.value);
                  adjustTextareaHeight();
                }}
                onKeyDown={handleKeyDown}
                placeholder={isDragging ? "Solte os arquivos aqui..." : "Digite sua mensagem... (Shift+Enter para nova linha)"}
                className="input w-full resize-none py-3 px-4 min-h-[48px] max-h-[200px]"
                disabled={isLoading}
                autoFocus
                rows={1}
                style={{ lineHeight: '1.5' }}
              />
            </div>
            <button
              type="submit"
              disabled={isLoading || (!inputMessage.trim() && pendingFiles.length === 0)}
              className="btn btn-primary h-12 px-6 flex-shrink-0"
            >
              {isLoading ? (
                <i className="fa-solid fa-spinner fa-spin"></i>
              ) : (
                <i className="fa-solid fa-paper-plane"></i>
              )}
            </button>
          </form>

          {/* Dica de formatos */}
          <div className="text-[10px] opacity-40 mt-2 text-center">
            Arraste arquivos ou clique em <i className="fa-solid fa-paperclip"></i> • PDF, DOC, TXT, Imagens (máx. 10MB)
          </div>
        </div>
      </div>
    </div>
  );
}
